import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, Trash2, Eye, LogOut, Edit, Plus, Download, Mail } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import DashboardStats from "@/components/DashboardStats";
import VisualPostEditor from "@/components/VisualPostEditor";
import DateRangeFilter from "@/components/DateRangeFilter";
import PublishedPostsTable from "@/components/PublishedPostsTable";
import NewsletterManager from "@/components/NewsletterManager";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface Post {
  id: number;
  title: string;
  slug: string;
  category: string;
  created_at: string;
  views: number;
  status: "draft" | "pending_review" | "published";
}

export default function AdminDashboard() {
  const { isAdmin, signOut, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [topic, setTopic] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletePostId, setDeletePostId] = useState<number | null>(null);
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate("/admin/login");
      return;
    }
    if (!authLoading && isAdmin) {
      loadPosts();
    }
  }, [isAdmin, authLoading, navigate]);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("posts")
        .select("id, title, slug, category, created_at, views, status")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (error) {
      console.error("Error loading posts:", error);
      toast.error("Error loading posts");
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePost = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!topic.trim()) {
      toast.error("Please enter a topic");
      return;
    }

    setIsGenerating(true);

    try {
      const { data, error } = await supabase.functions.invoke("generate-post", {
        body: { topic: topic.trim() },
      });

      if (error) throw error;

      if (data?.error) {
        toast.error(data.error);
        return;
      }

      toast.success("Post gerado com sucesso! Aguardando revisão.");
      setTopic("");
      await loadPosts();
    } catch (error: any) {
      console.error("Error generating post:", error);
      toast.error(error?.message || "Error generating post");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDeletePost = async () => {
    if (!deletePostId) return;

    try {
      const { error } = await supabase.from("posts").delete().eq("id", deletePostId);

      if (error) throw error;

      toast.success("Post deleted successfully");
      setDeletePostId(null);
      await loadPosts();
    } catch (error) {
      console.error("Error deleting post:", error);
      toast.error("Error deleting post");
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate("/");
  };

  const handleEditorSuccess = () => {
    setEditingPost(null);
    loadPosts();
  };

  const handleEditPost = (post: Post) => {
    setEditingPost(post);
  };

  const handleDateChange = (start: Date | null, end: Date | null) => {
    setStartDate(start);
    setEndDate(end);
  };

  const handleApprovePost = async (postId: number) => {
    try {
      const { error } = await supabase.from("posts").update({ status: "published" }).eq("id", postId);

      if (error) throw error;

      toast.success("Post aprovado e publicado!");
      await loadPosts();
    } catch (error) {
      console.error("Error approving post:", error);
      toast.error("Erro ao aprovar post");
    }
  };

  const handleRejectPost = async (postId: number) => {
    setDeletePostId(postId);
  };

  const exportToCSV = (data: { title: string }[], filename: string) => {
    const csvContent = ["Título", ...data.map((item) => `"${item.title.replace(/"/g, '""')}"`)].join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("CSV exportado com sucesso!");
  };

  const handleExportAll = () => {
    const data = posts.map((p) => ({ title: p.title }));
    exportToCSV(data, `todos-artigos-${new Date().toISOString().split("T")[0]}.csv`);
  };

  const handleExportPublished = () => {
    const data = posts.filter((p) => p.status === "published").map((p) => ({ title: p.title }));
    exportToCSV(data, `artigos-publicados-${new Date().toISOString().split("T")[0]}.csv`);
  };

  const pendingPosts = posts.filter((p) => p.status === "pending_review");
  const publishedPosts = posts.filter((p) => p.status === "published");
  const draftPosts = posts.filter((p) => p.status === "draft");

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto p-4 sm:p-6 max-w-7xl">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-3xl font-bold">Blog Dashboard</h1>
          <div className="flex gap-2 w-full sm:w-auto">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="flex-1 sm:flex-none">
                  <Download className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Exportar CSV</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-popover">
                <DropdownMenuItem onClick={handleExportAll}>Exportar todos os artigos</DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportPublished}>Exportar artigos publicados</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button onClick={handleLogout} variant="outline" size="sm" className="flex-1 sm:flex-none">
              <LogOut className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>

        <Tabs defaultValue="overview" className="space-y-4 sm:space-y-6">
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList className="inline-flex w-max sm:w-auto">
              <TabsTrigger value="overview" className="text-xs sm:text-sm">
                Visão Geral
              </TabsTrigger>
              <TabsTrigger value="pending" className="text-xs sm:text-sm">
                Pendentes {pendingPosts.length > 0 && `(${pendingPosts.length})`}
              </TabsTrigger>
              <TabsTrigger value="published" className="text-xs sm:text-sm">
                Publicados
              </TabsTrigger>
              <TabsTrigger value="newsletter" className="text-xs sm:text-sm">
                <Mail className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                Newsletter
              </TabsTrigger>
              <TabsTrigger value="generate" className="text-xs sm:text-sm">
                Gerar IA
              </TabsTrigger>
              <TabsTrigger value="editor" className="text-xs sm:text-sm">
                {editingPost ? "Editar" : "Novo"}
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="overview" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Filtro de Período</CardTitle>
                <CardDescription>Selecione o período para visualizar as estatísticas</CardDescription>
              </CardHeader>
              <CardContent>
                <DateRangeFilter onDateChange={handleDateChange} />
              </CardContent>
            </Card>
            <DashboardStats startDate={startDate} endDate={endDate} />
          </TabsContent>

          <TabsContent value="pending">
            <Card>
              <CardHeader>
                <CardTitle>Posts Aguardando Revisão</CardTitle>
                <CardDescription>Revise e aprove posts gerados pela IA antes de publicar</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : pendingPosts.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">Nenhum post aguardando revisão.</p>
                ) : (
                  <div className="space-y-4">
                    {pendingPosts.map((post) => (
                      <div
                        key={post.id}
                        className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg hover:bg-accent transition-colors gap-4"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-semibold text-sm sm:text-base line-clamp-2">{post.title}</h3>
                            <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 whitespace-nowrap">
                              Pendente
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-muted-foreground mt-1">
                            <span>{post.category}</span>
                            <span>{new Date(post.created_at).toLocaleDateString("pt-BR")}</span>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 sm:flex-none text-xs sm:text-sm"
                            onClick={() => {
                              handleEditPost(post);
                              document
                                .querySelector('[value="editor"]')
                                ?.dispatchEvent(new Event("click", { bubbles: true }));
                            }}
                          >
                            <Edit className="h-4 w-4 sm:mr-2" />
                            <span className="hidden sm:inline">Revisar e Editar</span>
                          </Button>
                          <Button
                            variant="default"
                            size="sm"
                            className="flex-1 sm:flex-none text-xs sm:text-sm"
                            onClick={() => handleApprovePost(post.id)}
                          >
                            Aprovar
                          </Button>
                          <Button variant="destructive" size="sm" onClick={() => handleRejectPost(post.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="published">
            <PublishedPostsTable
              onEdit={(post) => {
                handleEditPost(post);
                document.querySelector('[value="editor"]')?.dispatchEvent(new Event("click", { bubbles: true }));
              }}
              onDelete={(id) => setDeletePostId(id)}
              startDate={startDate}
              endDate={endDate}
            />
          </TabsContent>

          <TabsContent value="newsletter">
            <NewsletterManager />
          </TabsContent>

          <TabsContent value="generate">
            <Card>
              <CardHeader>
                <CardTitle>Gerar Post com IA</CardTitle>
                <CardDescription>
                  Digite um tema e gere um novo post com IA. O post será salvo como "Aguardando Revisão".
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleGeneratePost} className="flex flex-col sm:flex-row gap-4">
                  <Input
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    placeholder="Digite um tema (ex: Técnicas de meditação)"
                    disabled={isGenerating}
                    className="flex-1"
                  />
                  <Button type="submit" disabled={isGenerating || !topic} className="w-full sm:w-auto">
                    {isGenerating ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        <span className="hidden sm:inline">Gerando...</span>
                        <span className="sm:hidden">...</span>
                      </>
                    ) : (
                      <>
                        <Plus className="mr-2 h-4 w-4" />
                        Gerar Post
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="editor">
            <VisualPostEditor
              initialPost={editingPost}
              onSuccess={handleEditorSuccess}
              onCancel={() => setEditingPost(null)}
            />
          </TabsContent>
        </Tabs>
      </div>

      <AlertDialog open={deletePostId !== null} onOpenChange={() => setDeletePostId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the post.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeletePost}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
